import api from '@/shared/api';
import {
  buildTruthySearchParams,
  handleApiError,
  requestExcelBlobExport,
  type BulkDeleteResult,
  withQuery,
} from '@/shared/lib';
import type {
  Department,
  DepartmentResponse,
  SingleDepartmentResponse,
  CreateDepartmentDto,
  UpdateDepartmentDto,
  UpdateDepartmentPositionsDto,
  PaginatedDepartmentResponse,
  PaginationMetadata,
  DepartmentSearchParams,
} from '../model/types';
import type { AxiosResponse } from 'axios';

export interface ExportDepartmentsExcelParams {
  search?: string;
  sort?: string;
  order?: string;
}

const DEPARTMENTS_EXPORT_QUERY_KEYS = ['search', 'sort', 'order'] as const;

class DepartmentApi {
  async getAll(): Promise<Department[]> {
    try {
      const response: AxiosResponse<DepartmentResponse> = await api.get('/departments');
      return response.data.data || [];
    } catch (error) {
      console.error('Erro ao buscar departamentos:', error);
      throw error;
    }
  }

  async getById(id: number): Promise<Department> {
    try {
      const response: AxiosResponse<SingleDepartmentResponse> = await api.get(`/departments/${id}`);
      return response.data.data;
    } catch (error) {
      console.error('Erro ao buscar departamento:', error);
      throw error;
    }
  }

  async create(data: CreateDepartmentDto): Promise<Department> {
    try {
      const response: AxiosResponse<SingleDepartmentResponse> = await api.post(
        '/departments',
        data
      );
      return response.data.data;
    } catch (error: unknown) {
      throw handleApiError(error, 'Erro ao criar departamento');
    }
  }

  async update(id: number, data: UpdateDepartmentDto): Promise<Department> {
    try {
      const response: AxiosResponse<SingleDepartmentResponse> = await api.put(
        `/departments/${id}`,
        data
      );
      return response.data.data;
    } catch (error) {
      console.error('Erro ao atualizar departamento:', error);
      throw error;
    }
  }

  async updatePositions(id: number, data: UpdateDepartmentPositionsDto): Promise<Department> {
    try {
      const response: AxiosResponse<SingleDepartmentResponse> = await api.patch(
        `/departments/${id}/positions`,
        data
      );
      return response.data.data;
    } catch (error) {
      console.error('Erro ao atualizar cargos do departamento:', error);
      throw error;
    }
  }

  async delete(id: number): Promise<void> {
    try {
      await api.delete(`/departments/${id}`);
    } catch (error) {
      console.error('Erro ao deletar departamento:', error);
      throw error;
    }
  }

  async checkLinks(ids: number[]): Promise<{ hasLinks: boolean }> {
    try {
      const response = await api.post('/departments/links-check', { ids });
      return response.data.data;
    } catch (error: unknown) {
      throw handleApiError(error, 'Erro ao verificar vínculos dos departamentos');
    }
  }

  async bulkDelete(ids: number[]): Promise<{ data: BulkDeleteResult }> {
    try {
      const response = await api.delete('/departments/bulk', {
        data: { ids },
      });
      return { data: response.data.data };
    } catch (error: unknown) {
      throw handleApiError(error, 'Erro ao deletar departamentos em lote');
    }
  }

  async search(
    params: DepartmentSearchParams
  ): Promise<{ data: Department[]; metadata: PaginationMetadata }> {
    try {
      const response: AxiosResponse<PaginatedDepartmentResponse> = await api.get(
        withQuery('/departments', params)
      );

      return {
        data: response.data.data ?? [],
        metadata: response.data.metadata ?? {
          total: 0,
          page: 1,
          itemsPerPage: 10,
          totalPages: 0,
        },
      };
    } catch (error) {
      console.error('Erro ao buscar departamentos:', error);
      throw error;
    }
  }

  async exportDepartmentsExcel(
    params: ExportDepartmentsExcelParams
  ): Promise<{ blob: Blob; exportedCount: number; filename: string }> {
    const queryParams = buildTruthySearchParams(params, DEPARTMENTS_EXPORT_QUERY_KEYS);

    return requestExcelBlobExport(
      () =>
        api.get(`/departments/export/excel?${queryParams.toString()}`, {
          responseType: 'blob',
        }),
      'departamentos.xlsx',
      'Tente novamente.'
    );
  }
}

export const departmentApi = new DepartmentApi();
